import React from "react";
import { motion } from "framer-motion";
import { House, ConciergeBell, Sun, ArrowRight, ArrowDown } from "lucide-react";
import { ASSETS } from "../mock";

const pillars = [
  { icon: House, label: "Property Consultancy" },
  { icon: ConciergeBell, label: "Concierge Services" },
  { icon: Sun, label: "Marbella Lifestyle" },
];

const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          alt="Puerto Banus marina with yachts and La Concha mountain at sunset"
          className="w-full h-full object-cover"
          src={ASSETS.heroBanner}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#A9CBE8]/95 via-[#B7D4EC]/65 to-[#C8DEF2]/25" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B284A]/45 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pt-36 pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="uppercase tracking-[0.45em] text-xs text-[#8F6A24] font-semibold mb-8"
        >
          Marbella Consulting
        </motion.p>
        <h1 className="font-serif-lux font-semibold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.06] tracking-tight text-[#081F3A] max-w-3xl overflow-hidden">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              Property Consultancy
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-serif-lux not-italic font-semibold text-[#A87B28]">&amp;</span> Concierge Services
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-7 text-lg md:text-xl text-[#081F3A] max-w-xl leading-relaxed font-semibold"
        >
          Exclusive properties. Bespoke services.
          <span className="block">A seamless Marbella lifestyle.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-10 flex flex-wrap items-center gap-y-6"
        >
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                className={`flex items-center gap-4 pr-6 sm:pr-8 ${
                  i > 0 ? "pl-6 sm:pl-8 border-l border-[#B88A3B]/60" : ""
                }`}
              >
                <Icon
                  size={30}
                  className="text-[#A87B28] drop-shadow-[0_1px_1px_rgba(255,255,255,0.7)]"
                />
                <span className="uppercase tracking-[0.18em] text-xs sm:text-sm font-bold text-[#0B284A] leading-snug max-w-[100px] sm:max-w-[130px]">
                  {p.label}
                </span>
              </div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href="#services"
            className="group inline-flex items-center gap-3 bg-[#B88A3B] text-white px-8 py-4 uppercase tracking-[0.2em] text-sm font-medium hover:bg-[#C09245] hover:-translate-y-1 transition-[background-color,transform] duration-300"
          >
            Discover Our Services
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#properties"
            className="border border-[#0B284A]/40 text-[#0B284A] px-8 py-4 uppercase tracking-[0.2em] text-sm font-medium hover:border-[#B88A3B] hover:text-[#B88A3B] transition-colors duration-300"
          >
            View Properties
          </a>
        </motion.div>
      </div>

      <p className="absolute bottom-8 left-6 lg:left-12 uppercase tracking-[0.35em] text-[0.65rem] sm:text-xs text-[#F9F9F7] drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
        Your Trusted Partner in Marbella
      </p>
      <div className="absolute bottom-8 right-8 lg:right-12 hidden sm:flex items-center gap-3 text-[#F9F9F7] drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
        <span className="uppercase tracking-[0.3em] text-[0.65rem]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </div>
    </section>
  );
};

export default Hero;
